import { BetrokkenTrein, OngewensteGebeurtenis } from "@/entities/ongewenste-gebeurtenis";
import { Badge, Card, Icon, Label } from "@/shared/components";
import { cn, hourFormatInAmsterdam } from "@/shared/utils";

interface OngewensteGebeurtenisSummaryCardProps {
    ongewensteGebeurtenis: OngewensteGebeurtenis | undefined;
    betrokkenTreinen?: BetrokkenTrein[];
    className?: string;
    spoorwebStatus?: string;
}

export function OngewensteGebeurtenisSummaryCard({
    ongewensteGebeurtenis,
    betrokkenTreinen,
    className,
    spoorwebStatus,
}: Readonly<OngewensteGebeurtenisSummaryCardProps>) {
    if (!ongewensteGebeurtenis) return null;

    const prognose = ongewensteGebeurtenis.prognose?.datumTijd ? hourFormatInAmsterdam(ongewensteGebeurtenis.prognose.datumTijd) : "";
    const { nummer: spoorwebNr, planaanpassing } = ongewensteGebeurtenis;

    return (
        <Card className={cn(`border-l-content-info relative border-l-4 p-4 pl-12`, className)}>
            <Icon name="train-conflict-2" size="m" className="text-content-info absolute left-3" />

            <div className="mb-4 flex items-center justify-between gap-1">
                <h3 className="text-content-emphasis text-xl font-medium">{ongewensteGebeurtenis.displayNaam}</h3>
                <Badge variant="gray-subtle">#{spoorwebNr}</Badge>
            </div>
            <dl className="grid grid-cols-[162px_auto_1fr] gap-x-6 gap-y-2">
                <div>
                    <Label asChild className="mb-1">
                        <dt>Treindienstleidergebied</dt>
                    </Label>
                    <dd className="truncate" title={ongewensteGebeurtenis.treindienstLeiderGebied?.join(", ")}>
                        {ongewensteGebeurtenis.treindienstLeiderGebied?.join(", ")}
                    </dd>
                </div>
                <div>
                    <Label asChild className="mb-1">
                        <dt>TIS</dt>
                    </Label>
                    <dd>{ongewensteGebeurtenis.treinIncidentScenario?.code}</dd>
                </div>
                <div>
                    <Label asChild className="mb-1">
                        <dt>Prognose</dt>
                    </Label>
                    <dd>{prognose || <span className="italic">NB</span>}</dd>
                </div>
                {planaanpassing && (
                    <div className="col-span-3">
                        <Label asChild className="mb-1">
                            <dt>Planaanpassing</dt>
                        </Label>
                        <dd>{planaanpassing}</dd>
                    </div>
                )}
                {spoorwebStatus !== "Open" && (
                    <div className="col-span-1">
                        <Label asChild className="mb-1">
                            <dt>Spoorweb Status</dt>
                        </Label>
                        <dd>{spoorwebStatus}</dd>
                    </div>
                )}

                {Array.isArray(betrokkenTreinen) && betrokkenTreinen.length > 0 && (
                    <div className="col-span-3">
                        <Label asChild className="mb-1">
                            <dt>Betrokken NSR trein(en)</dt>
                        </Label>
                        {betrokkenTreinen.map((trein, i) => (
                            <dd className="mb-1 flex items-center gap-4" key={`${i}-${trein.code}`}>
                                <span className="basis-[90px]">
                                    {trein?.code}
                                    <span className="font-bold"> {trein?.treinNummer}</span>
                                </span>
                                {trein.handelingsVrij ? (
                                    <Badge variant="green-subtle">handelingsvrij</Badge>
                                ) : (
                                    <Badge variant="red-subtle">niet handelingsvrij</Badge>
                                )}
                            </dd>
                        ))}
                    </div>
                )}
            </dl>
        </Card>
    );
}
